import { Button, Card, Container } from "react-bootstrap";
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

import { ApplicationContext } from "../../contexts/ApplicationContext";
import { Ticket } from "../../services/EventService";
import { DateTime } from "luxon";
import { NumberFormatter } from "@internationalized/number";
import { useTranslation } from "react-i18next";

const APP_VARIANT = import.meta.env.VITE_APP_VARIANT;

const DEFAULT_TICKETS_PER_ROW = 3;

function resolveTicketsPerRow(value: unknown): number {
    if (value === null || value === undefined || value === '') {
        return DEFAULT_TICKETS_PER_ROW;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : DEFAULT_TICKETS_PER_ROW;
}

function sortTicketsByDisplayOrder(tickets: any[]): any[] {
    return [...tickets].sort((a, b) => {
        const orderA = a.display_order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.display_order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
    });
}

type TicketCardSectionProps = {
    tickets: any[];
    ticketsPerRow: number;
    currencyFormatter: NumberFormatter;
    onSelectTicket: (ticket: Ticket) => void;
};

function TicketCardSection({ tickets, ticketsPerRow, currencyFormatter, onSelectTicket }: TicketCardSectionProps) {
    const { t } = useTranslation();
    const sectionRef = useRef<HTMLDivElement>(null);
    const columns = ticketsPerRow > 0 ? ticketsPerRow : 1;

    const equalizeHeights = useCallback(() => {
        const section = sectionRef.current;
        if (!section) return;

        const cards = section.querySelectorAll<HTMLElement>('.event__ticket-card');
        cards.forEach((card) => {
            card.style.minHeight = '';
        });

        let maxHeight = 0;
        cards.forEach((card) => {
            maxHeight = Math.max(maxHeight, card.offsetHeight);
        });

        if (maxHeight > 0) {
            cards.forEach((card) => {
                card.style.minHeight = `${maxHeight}px`;
            });
        }
    }, []);

    useLayoutEffect(() => {
        equalizeHeights();

        const section = sectionRef.current;
        if (!section) return;

        const observer = new ResizeObserver(() => equalizeHeights());
        observer.observe(section);
        window.addEventListener('resize', equalizeHeights);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', equalizeHeights);
        };
    }, [tickets, ticketsPerRow, equalizeHeights, t]);

    if (!tickets.length) {
        return null;
    }

    return (
        <div
            ref={sectionRef}
            className="event__ticket-grid mb-4"
            style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gap: '1rem',
            }}
        >
            {tickets.map((ticket) => (
                <Card key={ticket.documentId} className="event__ticket-card h-100 w-100">
                    <Card.Body className="d-flex flex-column h-100">
                        <Card.Title>
                            {ticket.title}
                            {ticket.isKombiTicket &&
                                <span className="badge bg-primary ms-2">{t('registration.kombiTicketBadge')}</span>
                            }
                        </Card.Title>
                        <Card.Text
                            className="flex-grow-1"
                            dangerouslySetInnerHTML={{ __html: ticket.description ? ticket.description.replace(/\n/g, '<br />') : null }}
                        ></Card.Text>
                        <div>
                            <p className="mb-2">
                                <strong>{currencyFormatter.format(ticket.price / 100)}</strong>{' '}
                                <small>({t('registration.plusVAT')})</small>
                            </p>
                            <Button
                                onClick={() => onSelectTicket(ticket)}
                                variant={`${APP_VARIANT === 'QS' ? 'secondary rounded-0' : 'primary rounded-pill'}`}
                            >
                                {t('registration.secureTicket')}
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            ))}
        </div>
    );
}

function EventTicketsView() {
    const { t, i18n } = useTranslation();
    const { eventItem, setCurrentView, ticketCategory, setCurrentTicket, setWorkshopTickets } = useContext(ApplicationContext);

    const [tickets, setTickets] = useState([]);
    const [filteredTickets, setFilteredTickets] = useState([]); //tickets filtered by selected category

    const currencyFormatter = new NumberFormatter(i18n.language === 'en' ? 'en-US' : 'de-DE', {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const ticketsPerRow = resolveTicketsPerRow(eventItem?.tickets_per_row);

    const setupView = () => {
        if (document.body) {
            document.body.classList.remove('event-ticket-selected');
        }

        if (eventItem) {
            setTickets(eventItem.tickets);

            if (ticketCategory.length !== 0) {
                setFilteredTickets(eventItem.tickets.filter((t: any) => ticketCategory === t.ticket_category?.route));
            } else {
                setFilteredTickets(eventItem.tickets);
            }
            setWorkshopTickets(eventItem.tickets?.filter((t: any) => t.isWorkshop));
        }
    };

    useEffect(() => {
        if (Object.keys(eventItem).length === 0) return;

        setupView();
    }, [eventItem]);

    const ticketValid = (ticket: any) => {
        if (
            (!ticket.valid_from && !ticket.valid_until) ||
            (!ticket.valid_from && ticket.valid_until && DateTime.now() <= DateTime.fromISO(ticket.valid_until)) ||
            (!ticket.valid_until && ticket.valid_from && DateTime.now() >= DateTime.fromISO(ticket.valid_from)) ||
            (ticket.valid_from && ticket.valid_until && DateTime.now() >= DateTime.fromISO(ticket.valid_from) && DateTime.now() <= DateTime.fromISO(ticket.valid_until))
        ) {
            return true;
        }

        return false;
    }

    const showTicketReservationForm = (ticket: Ticket) => {
        const header = document.querySelector('.wp-site-blocks > header');
        let topOffset = 0;
        if (header) topOffset = header.getBoundingClientRect().height;

        scrollElementToOffset('event__registration-back-to-tickets', topOffset);
        setCurrentTicket(ticket);
        setCurrentView('eventViewRegistrationForm');
        if (document.body) {
            document.body.classList.add('event-ticket-selected');
        }
    }

    const scrollElementToOffset = (selector: string, offset = 100) => {
        const el = document.querySelector(selector);
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const scrollY = window.scrollY;

        const targetScrollY = scrollY + rect.top - offset;

        window.scrollTo({
            top: targetScrollY,
            behavior: 'smooth'
        });
    }

    const regularTickets = sortTicketsByDisplayOrder(
        filteredTickets?.filter((t: any) => ticketValid(t) && !t.isWorkshop) ?? []
    );
    const workshopTicketsList = sortTicketsByDisplayOrder(
        tickets?.filter((t: any) => ticketValid(t) && t.isWorkshop) ?? []
    );

    return (
        <Container>
            <TicketCardSection
                tickets={regularTickets}
                ticketsPerRow={ticketsPerRow}
                currencyFormatter={currencyFormatter}
                onSelectTicket={showTicketReservationForm}
            />
            {
                workshopTicketsList.length > 0 &&
                <h2 className="text-primary mb-2">{t('registration.workshops')}</h2>
            }
            <TicketCardSection
                tickets={workshopTicketsList}
                ticketsPerRow={ticketsPerRow}
                currencyFormatter={currencyFormatter}
                onSelectTicket={showTicketReservationForm}
            />
        </Container>
    );
}

export default EventTicketsView;
